import React, { useState } from 'react';
import {
  fetchStats,
  fetchRiepilogoUffici,
  fetchRiepilogoOperatori,
  esportaExcel as esportaExcelApi,
  RiepilogoUfficio,
  RiepilogoOperatore,
  Stats,
} from './administrationApi';
import {
  calcolaPaginazione,
  formattaNumeroDecimale,
  getInizioMeseCorrente,
  getDataCorrente,
} from './administrationUtils';

interface AdminPageProps {
  onLogout: () => void;
}

const AdminPage: React.FC<AdminPageProps> = ({ onLogout }) => {
  const [datiCaricati, setDatiCaricati] = useState(false);
  const [stats, setStats] = useState<Stats>({ ore_totali: 0, uffici_attivi: 0, operatori: 0 });
  const [datiUffici, setDatiUffici] = useState<RiepilogoUfficio[]>([]);
  const [datiOperatori, setDatiOperatori] = useState<RiepilogoOperatore[]>([]);
  const [dataInizio, setDataInizio] = useState(getInizioMeseCorrente());
  const [dataFine, setDataFine] = useState(getDataCorrente());
  
  // Paginazione per uffici
  const [paginaUffici, setPaginaUffici] = useState(1);
  const elementiPerPagina = 10;
  
  // Paginazione per operatori
  const [paginaOperatori, setPaginaOperatori] = useState(1);

  const caricaDati = async () => {
    try {
      const filtri = {
        dataInizio,
        dataFine,
      };

      // Carica tutti i dati in parallelo
      const [statsData, ufficiData, operatoriData] = await Promise.all([
        fetchStats(filtri),
        fetchRiepilogoUffici(filtri),
        fetchRiepilogoOperatori(filtri),
      ]);

      setStats(statsData);
      setDatiUffici(ufficiData);
      setDatiOperatori(operatoriData);
      setPaginaUffici(1);
      setPaginaOperatori(1);
      setDatiCaricati(true);
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
      alert('Errore nel caricamento dei dati. Riprova.');
    }
  };

  // Calcola elementi paginati per uffici
  const { elementiPaginati: ufficiPaginati, totalePagine: totalePagineUffici } = 
    calcolaPaginazione(datiUffici, paginaUffici, elementiPerPagina);

  // Calcola elementi paginati per operatori
  const { elementiPaginati: operatoriPaginati, totalePagine: totalePagineOperatori } = 
    calcolaPaginazione(datiOperatori, paginaOperatori, elementiPerPagina);

  const handleEsportaExcel = async (tipo: 'Uffici' | 'Operatori') => {
    try {
      await esportaExcelApi(tipo, {
        dataInizio,
        dataFine,
      });
    } catch (error) {
      console.error('Errore durante l\'esportazione:', error);
      alert('Errore durante l\'esportazione del file Excel. Riprova.');
    }
  };

  const handleEsportaExcelSingolo = async (tipo: 'Uffici' | 'Operatori', id: number) => {
    try {
      await esportaExcelApi(tipo, {
        dataInizio,
        dataFine,
        strutturaSelezionata: tipo === 'Uffici' ? id.toString() : undefined,
        operatoreSelezionato: tipo === 'Operatori' ? id.toString() : undefined,
      });
    } catch (error) {
      console.error('Errore durante l\'esportazione:', error);
      alert('Errore durante l\'esportazione del file Excel. Riprova.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-8 text-white flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div>
              <h1 className="text-3xl font-bold mb-2">🛠️ Amministrazione Timesheet</h1>
              <p className="opacity-90">Gestione ed esportazione dati</p>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all"
            >
              🚪 Esci
            </button>
          </div>

          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">🔍 Filtri di Ricerca</h2>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-purple-500">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">📅 Data Inizio</label>
                    <input 
                      type="date" 
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" 
                      value={dataInizio}
                      onChange={(e) => setDataInizio(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">📅 Data Fine</label>
                    <input 
                      type="date" 
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" 
                      value={dataFine}
                      onChange={(e) => setDataFine(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">&nbsp;</label>
                    <button
                      onClick={caricaDati}
                      className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all font-medium"
                    >
                      📊 Carica Dati
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {datiCaricati && (
              <>
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📈 Statistiche Generali</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-400 text-center">
                      <div className="text-4xl font-bold text-blue-600">{stats.ore_totali}</div>
                      <div className="text-blue-800 font-medium mt-1">Ore Totali</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-400 text-center">
                      <div className="text-4xl font-bold text-blue-600">{stats.uffici_attivi}</div>
                      <div className="text-blue-800 font-medium mt-1">Strutture Attive</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-400 text-center">
                      <div className="text-4xl font-bold text-blue-600">{stats.operatori}</div>
                      <div className="text-blue-800 font-medium mt-1">Operatori</div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📤 Esportazioni Excel</h2>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-blue-800 mb-4">
                    ℹ️ I file Excel verranno generati in base ai filtri selezionati sopra
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => handleEsportaExcel('Uffici')} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium">
                      📊 Esporta Totali per Struttura
                    </button>
                    <button onClick={() => handleEsportaExcel('Operatori')} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium">
                      👥 Esporta Totali per Operatore
                    </button>
                    {/* <button onClick={() => esportaExcel('Dettaglio')} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium">
                      📋 Esporta Dettaglio Completo
                    </button> */}
                  </div>
                </div>

                <div className="mb-8">
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="bg-gray-100 p-4 border-b-2 flex justify-between items-center">
                      <span className="font-semibold">🏢 Riepilogo per Struttura</span>
                      <span className="text-sm text-gray-600">{datiUffici.length} uffici</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left font-semibold text-gray-600">Struttura</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Ore Totali</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Giorni Lavorati</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Media Ore/Giorno</th>
                            <th className="p-3 text-center font-semibold text-gray-600">Export</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ufficiPaginati.map((d, i) => (
                            <tr key={i} className="border-b hover:bg-gray-50">
                              <td className="p-3"><strong>{d.uffici}</strong></td>
                              <td className="p-3">{d.ore_totali}h</td>
                              {/* <td className="p-3">{formattaNumeroDecimale(d.giorni_lavorati)}</td>
                              <td className="p-3">{formattaNumeroDecimale(d.media_ore_giorno)}h</td> */}
                              <td className="p-3">{d.giorni_lavorati}</td>
                              <td className="p-3">{d.media_ore_giorno}h</td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => handleEsportaExcelSingolo('Uffici', d.id_struttura)}
                                  className="inline-flex items-center justify-center p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-all"
                                  title="Esporta Excel per questa struttura"
                                >
                                  <span className="text-xl">📊</span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalePagineUffici > 1 && (
                      <div className="bg-gray-50 p-4 flex justify-center items-center gap-2">
                        <button
                          onClick={() => setPaginaUffici(Math.max(1, paginaUffici - 1))}
                          disabled={paginaUffici === 1}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          ← Precedente
                        </button>
                        <span className="text-sm text-gray-600">
                          Pagina {paginaUffici} di {totalePagineUffici}
                        </span>
                        <button
                          onClick={() => setPaginaUffici(Math.min(totalePagineUffici, paginaUffici + 1))}
                          disabled={paginaUffici === totalePagineUffici}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Successiva →
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-8">
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="bg-gray-100 p-4 border-b-2 flex justify-between items-center">
                      <span className="font-semibold">👥 Riepilogo per Operatore</span>
                      <span className="text-sm text-gray-600">{datiOperatori.length} operatori</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left font-semibold text-gray-600">Operatore</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Ore Totali</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Giorni Lavorati</th>
                            <th className="p-3 text-left font-semibold text-gray-600">N° Strutture</th>
                            <th className="p-3 text-left font-semibold text-gray-600">Strutture</th>
                            <th className="p-3 text-center font-semibold text-gray-600">Export</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operatoriPaginati.map((d, i) => (
                            <tr key={i} className="border-b hover:bg-gray-50">
                              <td className="p-3"><strong>{d.operatore}</strong></td>
                              <td className="p-3">{d.ore_totali}h</td>
                              <td className="p-3">{d.giorni_lavorati}</td>
                              <td className="p-3">{d.numero_uffici}</td>
                              <td className="p-3 relative group">
                                {d.strutture.length > 30 ? (
                                  <>
                                    <span className="cursor-help">{d.strutture.substring(0, 30)}...</span>
                                    <div className="invisible group-hover:visible absolute z-10 bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg left-0 top-full mt-1 whitespace-normal max-w-md">
                                      {d.strutture}
                                    </div>
                                  </>
                                ) : (
                                  d.strutture
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => handleEsportaExcelSingolo('Operatori', d.id_operatore)}
                                  className="inline-flex items-center justify-center p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-all"
                                  title="Esporta Excel per questo operatore"
                                >
                                  <span className="text-xl">📊</span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalePagineOperatori > 1 && (
                      <div className="bg-gray-50 p-4 flex justify-center items-center gap-2">
                        <button
                          onClick={() => setPaginaOperatori(Math.max(1, paginaOperatori - 1))}
                          disabled={paginaOperatori === 1}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          ← Precedente
                        </button>
                        <span className="text-sm text-gray-600">
                          Pagina {paginaOperatori} di {totalePagineOperatori}
                        </span>
                        <button
                          onClick={() => setPaginaOperatori(Math.min(totalePagineOperatori, paginaOperatori + 1))}
                          disabled={paginaOperatori === totalePagineOperatori}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Successiva →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!datiCaricati && (
              <div className="text-center py-12 text-gray-400">
                <p className="text-xl mb-2">📊</p>
                <p>Carica i dati per visualizzare statistiche e report</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
